"use client";

import { useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Container from "@/components/ui/container";
import { products } from "@/lib/data";
import { formatMoney } from "@/lib/format";

export default function Recommended() {
  const items = products.slice(0, 10);
  const trackRef = useRef<HTMLUListElement>(null);

  const scrollBy = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const step = track.clientWidth * 0.7;
    track.scrollBy({ left: step * direction, behavior: "smooth" });
  };

  return (
    <section className="py-12 sm:py-16">
      <Container>
        <div className="flex items-center gap-4 mb-8 sm:mb-10">
          <span className="flex-1 h-px bg-[color:var(--color-border)]" />
          <h2 className="text-xl sm:text-2xl font-semibold text-[color:var(--color-text)] whitespace-nowrap">
            Recommended for you
          </h2>
          <span className="flex-1 h-px bg-[color:var(--color-border)]" />
        </div>
      </Container>

      <div className="relative">
        <Container>
          <ul
            ref={trackRef}
            className="grid grid-flow-col auto-cols-[45%] sm:auto-cols-[30%] md:auto-cols-[22%] lg:auto-cols-[14.285%] gap-4 sm:gap-5 overflow-x-auto snap-x snap-mandatory pb-3 px-1 no-scrollbar"
          >
            {items.map((p) => {
              const price = formatMoney(p.priceRange.minVariantPrice);
              return (
                <li key={p.id} className="snap-start">
                  <Link
                    href={`/products/${p.handle}`}
                    className="group block text-center"
                  >
                    <div className="relative aspect-square mb-3 bg-[color:var(--color-surface-alt)] overflow-hidden">
                      <Image
                        src={p.featuredImage.url}
                        alt={p.featuredImage.altText ?? p.title}
                        fill
                        sizes="(min-width: 1024px) 14vw, (min-width: 640px) 30vw, 45vw"
                        className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                    </div>
                    <h3 className="text-sm font-semibold leading-snug line-clamp-2 min-h-[2.5rem] text-[color:var(--color-text)] group-hover:text-[color:var(--color-primary)]">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm font-semibold text-[color:var(--color-text)]">
                      {price}{" "}
                      <span className="text-xs font-normal text-[color:var(--color-muted)]">
                        inc. VAT
                      </span>
                    </p>
                  </Link>
                </li>
              );
            })}
          </ul>
        </Container>

        <button
          type="button"
          aria-label="Previous recommendations"
          onClick={() => scrollBy(-1)}
          className="absolute left-2 sm:left-4 top-[32%] -translate-y-1/2 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[color:var(--color-sale)] text-white shadow-lift hover:bg-[color:var(--color-sale-dark)]"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          type="button"
          aria-label="Next recommendations"
          onClick={() => scrollBy(1)}
          className="absolute right-2 sm:right-4 top-[32%] -translate-y-1/2 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[color:var(--color-sale)] text-white shadow-lift hover:bg-[color:var(--color-sale-dark)]"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </section>
  );
}
